import { PDFDocument } from "pdf-lib";
import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { planRedaction, sha256Hex, type TextItem } from "./redact-core.js";
import { rememberTokens, tokenMapFromVault } from "./vault.js";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface RedactedUpload {
  /** A new PDF made of page images with the sensitive numbers painted over. */
  pdf: Uint8Array;
  redactedText: string;
  tokens: Record<string, string>;
  pageCount: number;
  contentHash: string;
  found: number;
}

const SCALE = 2; // ~144 dpi: readable for Claude, small enough to upload

/**
 * Decision 6, option A: redact in the browser, no model involved. The text
 * layer is scanned with the shared rules; every matching item is painted
 * over on a rendered image of the page; the upload is those images plus the
 * redacted text. The original file never leaves the device.
 */
export async function redactPdf(file: File, onProgress?: (page: number, pages: number) => void): Promise<RedactedUpload> {
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  const pageCount = doc.numPages;

  // Pass 1: text with positions.
  const pages: TextItem[][] = [];
  const viewports: pdfjs.PageViewport[] = [];
  for (let n = 1; n <= pageCount; n++) {
    const page = await doc.getPage(n);
    const content = await page.getTextContent();
    const items: TextItem[] = [];
    for (const raw of content.items) {
      if (!("str" in raw)) continue;
      const [a, b, , , e, f] = raw.transform as number[];
      const height = raw.height || Math.hypot(a ?? 0, b ?? 0);
      items.push({ str: raw.str, x: e ?? 0, y: f ?? 0, width: raw.width, height, eol: raw.hasEOL });
    }
    pages.push(items);
    viewports.push(page.getViewport({ scale: SCALE }));
  }
  if (pages.every((p) => p.length === 0)) {
    throw new Error("This PDF has no text layer (it may be scanned). Scanned statements are not supported yet.");
  }
  // Tokens come from this device's local vault so they stay stable across
  // statements; the vault never leaves the browser.
  const tokenMap = tokenMapFromVault();
  // Reference numbers keep their last four digits readable; cards and accounts never do.
  const plan = planRedaction(pages, tokenMap, { visibleTail: 4 });
  rememberTokens(tokenMap);

  // Pass 2: render each page, paint the boxes, keep as JPEG.
  const out = await PDFDocument.create();
  for (let n = 1; n <= pageCount; n++) {
    onProgress?.(n, pageCount);
    const page = await doc.getPage(n);
    const viewport = viewports[n - 1]!;
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext("2d")!;
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    ctx.fillStyle = "#000";
    for (const box of plan.pages[n - 1]!.boxes) {
      const [x1, y1] = viewport.convertToViewportPoint(box.x, box.y + box.height);
      const [x2, y2] = viewport.convertToViewportPoint(box.x + box.width, box.y);
      ctx.fillRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    }
    const jpeg = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("could not encode page"))), "image/jpeg", 0.85),
    );
    const img = await out.embedJpg(await jpeg.arrayBuffer());
    const pdfPage = out.addPage([viewport.width / SCALE, viewport.height / SCALE]);
    pdfPage.drawImage(img, { x: 0, y: 0, width: pdfPage.getWidth(), height: pdfPage.getHeight() });
  }
  out.setTitle(`${file.name} (redacted)`);
  const pdf = await out.save();

  // Fail closed: the new file must carry no text layer at all.
  const check = await pdfjs.getDocument({ data: pdf.slice() }).promise;
  for (let n = 1; n <= check.numPages; n++) {
    const content = await (await check.getPage(n)).getTextContent();
    if (content.items.some((i) => "str" in i && i.str.trim())) {
      throw new Error("redacted file unexpectedly contains text; refusing to upload");
    }
  }

  return {
    pdf,
    redactedText: plan.text,
    tokens: plan.tokens,
    pageCount,
    contentHash: await sha256Hex(plan.text),
    found: plan.found,
  };
}
